import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap } from "@react-google-maps/api";
import { useTargetingMapLoader, mapContainerStyle, baseMapOptions } from "./loader";
import { animateMapPan, type PanAnim } from "./mapPan";
import { CenteredSpinner } from "./MapStates";
import { AreaAddedToast } from "./AreaAddedToast";

interface MapInstanceValue {
  /** The Google map instance, or null until it has loaded. */
  map: google.maps.Map | null;
  /** Smoothly pans the map to a coordinate (eased, distance-independent). */
  panTo: (target: { lat: number; lng: number }) => void;
}

const MapInstanceContext = createContext<MapInstanceValue | undefined>(undefined);

// Map instance access for the channel layers (pins / polygons). Available only
// inside a MapFrame.
export const useMapInstance = () => {
  const ctx = useContext(MapInstanceContext);
  if (ctx === undefined) throw new Error("useMapInstance must be used within a MapFrame");
  return ctx;
};

interface MapFrameProps {
  /** Stable selector for the map container (e.g. "targeting-map-d2d"). */
  cypressId: string;
  center?: { lat: number; lng: number };
  zoom?: number;
  /** Extra Google map options merged over the shared defaults. */
  options?: google.maps.MapOptions;
  /** Map click handler (e.g. layers use it to dismiss a tooltip). */
  onMapClick?: () => void;
  /** Map children — markers / overlays rendered inside the GoogleMap. */
  children?: React.ReactNode;
  /** Layer chrome rendered over the map (loading / error / empty states). */
  overlays?: React.ReactNode;
}

// The shared targeting-map shell: owns the Google script load, the GoogleMap
// instance, the eased pan helper, and the "New distribution area added" toast.
// Each channel renders its own layer (pins, polygons) as children + overlays;
// everything around the layer is identical across channels and lives here.
export const MapFrame = ({ cypressId, center, zoom = 12, options, onMapClick, children, overlays }: MapFrameProps) => {
  const { isLoaded } = useTargetingMapLoader();
  const [map, setMap] = useState<google.maps.Map | null>(null);
  const panRef = useRef<PanAnim>({ current: null });

  const panTo = useCallback(
    (target: { lat: number; lng: number }) => {
      if (map) animateMapPan(map, panRef.current, target);
    },
    [map]
  );

  useEffect(() => {
    return () => {
      if (panRef.current.current) {
        panRef.current.current.cancelled = true;
        cancelAnimationFrame(panRef.current.current.raf);
      }
    };
  }, []);

  const mapOptions = useMemo(() => ({ ...baseMapOptions, ...options }), [options]);
  const value = useMemo<MapInstanceValue>(() => ({ map, panTo }), [map, panTo]);

  if (!isLoaded) return <CenteredSpinner label="Loading map" />;

  return (
    <MapInstanceContext.Provider value={value}>
      <div className="relative h-full w-full" data-cypress-id={cypressId}>
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={center}
          zoom={zoom}
          options={mapOptions}
          onClick={onMapClick}
          onLoad={setMap}
          onUnmount={() => setMap(null)}
        >
          {children}
        </GoogleMap>
        {overlays}
        <AreaAddedToast />
      </div>
    </MapInstanceContext.Provider>
  );
};
